import { ActionType } from '@editor/src/js/constants/common-action-type';
import { TextPadding } from '@src/slide/domain/ui-model/element-modeles/property/text/constants/text-style-type';
import i18n from '@i18next/i18n';
import { FormPanelKey } from '@tencent/chart-drawer-components/lib/schema';
import { ChartElementType } from '@src/slide/domain/ui-model/element-modeles/Interface/chart-v3/chart-interaction.interface';

const DrawerName = {
    ANIMATION_RIGHT_DRAWER: 'animation-right-drawer',
    SLIDE_PROPERTY_DRAWER: 'slide-property-drawer',
    SELECT_WINDOW_RIGHT_DRAWER: 'select-window-right-drawer',
    SLIDE_RATING_DRAWER: 'slide-rating-drawer',
    AUTH: 'docs-component-collaborative-management', // 文档权限右侧侧边栏
    REPLACE_IMAGE: 'replace-image', // 图片替换侧边栏
    COMMENT_DRAWER: 'comment-drawer', // 批注侧边栏
    MACHINE_AUDIT: 'machine-audit', // 模版机器审核
};

export type IDrawerType = typeof DrawerName[keyof typeof DrawerName];

const FloatName = {
    RIGHT_SIDE_BAR_FLOAT_WIDGET: 'right-side-bar-float-widget',
};

const FormSectionCollapsed = '$formSectionCollapsed';

const FormLabelName = {
    [ActionType.SHADOW]: i18n.t('阴影'),
    [ActionType.REFLECTION]: i18n.t('映像'),
    [ActionType.GLOW]: i18n.t('发光'),
    [ActionType.SOFTEDGE]: i18n.t('柔化边缘'),
    [ActionType.FONT_SHADOW]: i18n.t('阴影 '), // 文字带个空白，否则和上面的阴影一样，会触发workbench bug，即两个菜单块联动
    [ActionType.FONT_REFLECTION]: i18n.t('映像 '),
    [ActionType.FONT_GLOW]: i18n.t('发光 '),
    FILL: i18n.t('填充'),
    [ActionType.FONT_FILL]: i18n.t('填充 '),
    [ActionType.FONT_BORDER]: i18n.t('轮廓 '),
    BORDER: i18n.t('边框'),
    [ActionType.FONT]: i18n.t('字体'),
    PARAGRAPH: i18n.t('段落'),
    TEXTBOX: i18n.t('文本框'),
    ALIGN: i18n.t('对齐'),
    LEVEL: i18n.t('层级'),
    GROUP: i18n.t('组合'),
    SIZE: i18n.t('大小'),
    CELL_SIZE: i18n.t('单元格大小'),
    ROTATE: i18n.t('旋转'),
    PICTURE_OPACITY: i18n.t('图片透明度'),
    PICTURE_FIX: i18n.t('图片校正'),
    PICTURE_EFFECT: i18n.t('图片色彩'),
    PICTURE_CROP: i18n.t('裁剪'),
    [ActionType.BORDERSTYLE]: i18n.t('边框'),
    SLIDE_SIZE: i18n.t('幻灯片比例'),
    BACKGROUND: i18n.t('背景'),
    LAYOUT: i18n.t('版式'),
    FOOTER: i18n.t('页脚'),
    POSITION: i18n.t('位置'),
    THREE_D_PROPERTIES: i18n.t('三维属性'),
};

const FormPanelName = {
    SHAPE: 'shape',
    TEXT: 'text',
};

const FormItemName = {
    /** 效果 */
    SHADOW: ActionType.SHADOW,
    SHADOW_SIZE: 'shadow-size',
    SHADOW_BLUR: 'shadow-blur',
    SHADOW_DISTANCE: 'shadow-distance',
    SHADOW_OPICITY: 'shadow-opacity',
    SHADOW_ANGLE: 'shadow-angle',
    SHADOW_ANGLE_TEXT: 'shadow-angle-text',
    SHADOW_COLOR: 'shadow-color',
    SHADOW_COLOR_TEXT: 'shadow-color-text',
    FILL_PRESET: 'fill-preset',
    FILL_SELECT: 'fill-select',
    SOFT_EDGE: ActionType.SOFTEDGE,
    SOFT_EDGE_SIZE: 'soft-edge-size',
    REFLECTION: ActionType.REFLECTION,
    REFLECTION_SIZE: 'reflection-size',
    REFLECTION_BLUR: 'reflection-blur',
    REFLECTION_DISTANCE: 'reflection-distance',
    REFLECTION_OPICITY: 'reflection-opacity',
    GLOW: ActionType.GLOW,
    GLOW_SIZE: 'glow-size',
    GLOW_OPICITY: 'glow-opacity',
    GLOW_COLOR: 'glow-color',
    GLOW_COLOR_TEXT: 'glow-color-text',
    /** 文字填充 **/
    FONT_FILL_PRESET: 'font-fill-preset',
    FONT_FILL_SELECT: 'font-fill-select',
    FONT_SOLID_FILL_COLOR: 'font-solid-fill-color',
    FONT_SOLID_FILL_COLOR_TEXT: 'font-solid-fill-color-text',
    FONT_SOLID_FILL_OPACITY: 'font-solid-fill-opacity',
    FONT_GRADUAL_FILL_TYPE_TEXT: 'font-gradual-fill-type-text',
    FONT_GRADUAL_FILL_LINEAR_GRADIENT: 'font-gradual-fill-linear-gradient',
    FONT_GRADUAL_FILL_RADIAL_GRADIENT: 'font-gradual-fill-radial-gradient',
    FONT_GRADUAL_FILL_RECT_GRADIENT: 'font-gradual-fill-rect-gradient',
    FONT_GRADUAL_FILL_SHAPE_GRADIENT: 'font-gradual-fill-shape-gradient',
    FONT_GRADUAL_FILL_ANGLE: 'font-gradual-fill-angle',
    FONT_GRADUAL_FILL_ANGLE_TEXT: 'font-gradual-fill-angle-text',
    FONT_GRADUAL_FILL_DIRECTION: 'font-gradual-fill-direction',
    FONT_GRADUAL_FILL_DIRECTION_TEXT: 'font-gradual-fill-direction-text',
    FONT_GRADUAL_FILL_BAR: 'font-gradual-fill-bar',
    FONT_GRADUAL_FILL_OPACITY: 'font-gradual-fill-opacity',
    FONT_IMAGE_FILL_UPLOAD: 'font-image-fill-upload',
    FONT_IMAGE_FILL_CLIPBOARD: 'font-image-fill-clipboard',
    FONT_IMAGE_FILL_STYLE: 'font-image-fill-style',
    FONT_IMAGE_FILL_OPACITY: 'font-image-fill-opacity',
    /** 文字边框 **/
    FONT_BORDER: ActionType.FONT_BORDER,
    FONT_BORDER_PRESET: 'font-border-preset',
    FONT_BORDER_SELECT: 'font-border-select',
    FONT_BORDER_LINE: 'font-border-line',
    FONT_BORDER_LINE_TEXT: 'font-border-line-text',
    FONT_BORDER_WIDTH: 'font-border-width',
    FONT_BORDER_WIDTH_TEXT: 'font-border-width-text',
    FONT_BORDER_COLOR: 'font-border-color',
    FONT_BORDER_COLOR_TEXT: 'font-border-color-text',
    FONT_BORDER_OPACITY: 'font-border-opacity',
    FONT_BORDER_GRADUAL_LINEAR_GRADIENT: 'font-border-gradual-linear-gradient',
    FONT_BORDER_GRADUAL_RADIAL_GRADIENT: 'font-border-gradual-radial-gradient',
    FONT_BORDER_GRADUAL_RECT_GRADIENT: 'font-border-gradual-rect-gradient',
    FONT_BORDER_GRADUAL_SHAPE_GRADIENT: 'font-border-gradual-shape-gradient',
    FONT_BORDER_GRADUAL_ANGLE: 'font-border-gradual-angle',
    FONT_BORDER_GRADUAL_DIRECTION: 'font-border-gradual-direction',
    FONT_BORDER_GRADUAL_TYPE_TEXT: 'font-border-gradual-type-text',
    FONT_BORDER_GRADUAL_ANGLE_TEXT: 'font-border-gradual-angle-text',
    FONT_BORDER_GRADUAL_DIRECTION_TEXT: 'font-border-gradual-direction-text',
    FONT_BORDER_GRADUAL_BAR: 'font-border-gradual-bar',
    FONT_BORDER_GRADUAL_OPACITY: 'font-border-gradual-opacity',
    FONT_BORDER_SPACE: 'font-border-space',
    /** 文字效果 **/
    FONT_SHADOW: ActionType.FONT_SHADOW,
    FONT_SHADOW_SIZE: 'font-shadow-size',
    FONT_SHADOW_BLUR: 'font-shadow-blur',
    FONT_SHADOW_DISTANCE: 'font-shadow-distance',
    FONT_SHADOW_OPACITY: 'font-shadow-opacity',
    FONT_SHADOW_ANGLE: 'font-shadow-angle',
    FONT_SHADOW_ANGLE_TEXT: 'font-shadow-angle-text',
    FONT_SHADOW_COLOR: 'font-shadow-color',
    FONT_SHADOW_COLOR_TEXT: 'font-shadow-color-text',
    FONT_REFLECTION: ActionType.FONT_REFLECTION,
    FONT_REFLECTION_SIZE: 'font-reflection-size',
    FONT_REFLECTION_BLUR: 'font-reflection-blur',
    FONT_REFLECTION_DISTANCE: 'font-reflection-distance',
    FONT_REFLECTION_OPACITY: 'font-reflection-opacity',
    FONT_GLOW: ActionType.FONT_GLOW,
    FONT_GLOW_SIZE: 'font-glow-size',
    FONT_GLOW_OPACITY: 'font-glow-opacity',
    FONT_GLOW_COLOR: 'font-glow-color',
    FONT_GLOW_COLOR_TEXT: 'font-glow-color-text',
    /** 字体 */
    FONT: ActionType.FONT,
    FONT_THEME: ActionType.FONT_THEME,
    FONT_COLOR: ActionType.FORECOLOR,
    FONT_SIZE: ActionType.FONTSIZE,
    FONT_BACK_COLOR: ActionType.BACKCOLOR,
    FONT_ADJUST_TEXT: 'font-adjust-text',
    FONT_ADJUST: 'font-adjust',
    FONT_PADDING_TEXT: 'font-padding-text',
    FONT_PADDING_UNIT: 'font-padding-unit',
    FONT_PADDING_LEFT: TextPadding.PADDING_LEFT,
    FONT_PADDING_RIGHT: TextPadding.PADDING_RIGHT,
    FONT_PADDING_TOP: TextPadding.PADDING_TOP,
    FONT_PADDING_BOTTOM: TextPadding.PADDING_BOTTOM,
    FONT_AUTO_BREAK: 'font-auto-break',
    TEXT_DIRECTION_LABEL: 'text-direction-label',
    TEXT_DIRECTION: 'text-direction',
    ORDER_OF_LINE_LABEL: 'order-of-line-label',
    ORDER_OF_LINE: 'order-of-line',
    /** 段落 */
    ITEM_NUMBER: ActionType.ITEMNUMBER,
    DIGIT_NUMBER: ActionType.DIGITNUMBER,
    ROW_SPACE: ActionType.ROW_SPACE,
    /** 布局 */
    SIZE_ADJUST: ActionType.SIZE_ADJUST,
    SELECT_WINDOW: ActionType.SELECT_WINDOW,
    /** 层级 */
    LAYOUT_ROTATE: 'layout-rotate',
    /** 3D */
    RESET_3D: 'reset-3d',
    ROTATION: 'rotation',
    ANGLE_X: 'angle-x',
    ANGLE_Y: 'angle-y',
    ANGLE_Z: 'angle-z',
    LAYOUT_ROTATE_3D_X: 'layout-rotate-3d-x',
    LAYOUT_ROTATE_3D_Y: 'layout-rotate-3d-y',
    LAYOUT_ROTATE_3D_Z: 'layout-rotate-3d-z',
    SCALE_DEPTH_3D: 'scale-depth-3d',
    STYLE_3D: 'style-3d',
    DEPTH_3D: 'depth-3d',
    CHANGE_DEPTH_COLOR_3D: 'change-depth-color-3d',
    DEPTH_COLOR_3D: 'depth-color-3d',
    SHOW_FLAT_TEXT: 'show-flat-text',
    FRUSTUM_3D: 'frustum-3d',
    FRONT_FRUSTUM_3D_WIDTH: 'front-frustum-3d-width',
    SCALE_FRONT_FRUSTUM_3D_WIDTH: 'scale-front-frustum-3d-width',
    FRONT_FRUSTUM_3D_HEIGHT: 'front-frustum-3d-height',
    SCALE_FRONT_FRUSTUM_3D_HEIGHT: 'scale-front-frustum-3d-height',
    BACK_FRUSTUM_3D_WIDTH: 'back-frustum-3d',
    SCALE_BACK_FRUSTUM_3D_WIDTH: 'scale-back-frustum-3d',
    BACK_FRUSTUM_3D_HEIGHT: 'back-frustum-3d-height',
    SCALE_BACK_FRUSTUM_3D_HEIGHT: 'scale-back-frustum-3d-height',
    LIGHT_3D: 'light-3d',
    LIGHT_TYPE_3D: 'light-type-3d-name',
    CHANGE_3D_LIGHT_TYPE: 'change-3d-light-type',
    LIGHT_ROTATION: 'light-rotation',
    CHANGE_LIGHT_ROTATION_ANGLE: 'change-light-rotation-angle',
    MATERIAL_3D: 'material-3d',
    MATERIAL_TYPE: 'material-type',
    CHANGE_MATERIAL_TYPE: 'change-material-type',
    CAMERA_PRESET_3D: 'camera-preset-3d',
    CHANGE_CAMERA_PRESET: 'change-camera-preset-3d',
    /** 布局 - 位置 */
    HORIZONTAL_POSITION_TEXT: 'horizontal-position-text',
    VERTICAL_POSITION_TEXT: 'vertical-position-text',
    HORIZONTAL_POSITION: 'horizontal-position',
    VERTICAL_POSITION: 'vertical-position',
    HORIZONTAL_FROM: 'horizontal-from',
    VERTICAL_FROM: 'vertical-from',
    /** 图片 */
    PICTURE_FIX_RESET: 'picture-fix-reset', // 图片校正,重置
    SHARPEN_SOFTEN: 'sharpen-soften', // 清晰度
    BRIGHTNESS: 'brightness', // 亮度
    CONSTRAST: 'contrast', // 对比度
    SATURATE: 'saturate', // 饱和度
    COLOR_TEMPERATURE: 'colorTemperature', // 色温
    PICTURE_RECOLOR: 'recolor', // 重新着色
    PICTURE_RECOLOR_TEXT: 'recolor-text', // 重新着色文字
    PICTURE_COLOR_RESET: 'picture-color-reset', // 图片颜色,重置
    PICTURE_OPACITY: 'picture-opacity', // 图片透明度
    PICTURE_TAILOR: ActionType.TAILOR_PIC,
    PICTURE_POSITION_TEXT: 'picture-position-text',  // 图片位置
    PICTURE_POSITIOIN_HEIGHT: 'picture-position-height',  // 图片位置-高度
    PICTURE_POSITIOIN_WIDTH: 'picture-position-width',  // 图片位置-宽度
    PICTURE_POSITIOIN_OFFSET_X: 'picture-position-offsetX',  // 图片位置-偏移量X
    PICTURE_POSITIOIN_OFFSET_Y: 'picture-position-offsetY',  // 图片位置-偏移量Y
    CROP_POSITION_TEXT: 'crop-position-text',  // 裁剪位置
    CROP_POSITIOIN_HEIGHT: 'crop-position-height',  // 裁剪位置-高度
    CROP_POSITIOIN_WIDTH: 'crop-position-width',  // 裁剪位置-宽度
    CROP_POSITIOIN_LEFT: 'crop-position-left',  // 裁剪位置-左
    CROP_POSITIOIN_TOP: 'crop-position-top',  // 裁剪位置-上
    /** 填充 */
    SOLID_FILL_COLOR: 'solid-fill-color',
    SOLID_FILL_COLOR_TEXT: 'solid-fill-color-text',
    SOLID_FILL_OPACITY: 'solid-fill-opacity',
    GRADUAL_FILL_TYPE_TEXT: 'gradual-fill-type-text',
    GRADUAL_FILL_LINEAR_GRADIENT: 'gradual-fill-linear-gradient',
    GRADUAL_FILL_RADIAL_GRADIENT: 'gradual-fill-radial-gradient',
    GRADUAL_FILL_RECT_GRADIENT: 'gradual-fill-rect-gradient',
    GRADUAL_FILL_SHAPE_GRADIENT: 'gradual-fill-shape-gradient',
    GRADUAL_FILL_TITLE_GRADIENT: 'gradual-fill-title-gradient',
    GRADUAL_FILL_ANGLE_TEXT: 'gradual-fill-angle-text',
    GRADUAL_FILL_ANGLE: 'gradual-fill-angle',
    GRADUAL_FILL_DIRECTION_TEXT: 'gradual-fill-direction-text',
    GRADUAL_FILL_DIRECTION: 'gradual-fill-direction',
    GRADUAL_FILL_BAR: 'gradual-fill-bar',
    GRADUAL_FILL_OPACITY: 'gradual-fill-opacity',
    GRADUAL_FILL_ROTATE_WITH_SHAPE: 'gradual-fill-rotate-width-shape',
    IMAGE_FILL_ROTATE_WITH_SHAPE: 'image-fill-rotate-width-shape',
    IMAGE_FILL_UPLOAD: 'image-fill-upload',
    IMAGE_FILL_CLIPBOARD: 'image-fill-clipboard',
    IMAGE_FILL_OPACITY: 'image-fill-opacity',
    IMAGE_FILL_STYLE: 'image-fill-style',
    RESET_BACKGROUND: 'reset-background',
    APPLY_TO_ALL_SLIDE: 'apply-to-all-slide',
    /** 边框 */
    BORDER_PRESET: 'border-preset',
    BORDER_SELECT: 'border-select',
    BORDER_LINE: 'border-line',
    BORDER_LINE_TEXT: 'border-line-text',
    BORDER_WIDTH: 'border-width',
    BORDER_WIDTH_TEXT: 'border-width-text',
    BORDER_COLOR: 'border-color',
    BORDER_COLOR_TEXT: 'border-color-text',
    BORDER_OPACITY: 'border-opacity',
    BORDER_ARROW_TEXT: 'border-arrow-text',
    BORDER_ARROW_HEAD: 'border-arrow-head',
    BORDER_ARROW_TAIL: 'border-arrow-tail',
    BORDER_ARROW_SIZE_TEXT: 'border-arrow-size-text',
    BORDER_ARROW_SIZE_HEAD: 'border-arrow-size-head',
    BORDER_ARROW_SIZE_TAIL: 'border-arrow-size-tail',
    BORDER_SKETCH_TEXT: 'border-sketch-text',
    BORDER_SKETCH: 'border-sketch',
    BORDER_LINE_WIDTH: 'border-line-width',
    BORDER_LINE_STYLE: 'border-line-style',
    BORDER_ARROW_STYLE: 'border-arrow-style',
    BORDER_GRADUAL_TYPE_TEXT: 'border-gradual-type-text',
    BORDER_GRADUAL_LINEAR_GRADIENT: 'border-gradual-linear-gradient',
    BORDER_GRADUAL_RADIAL_GRADIENT: 'border-gradual-radial-gradient',
    BORDER_GRADUAL_RECT_GRADIENT: 'border-gradual-rect-gradient',
    BORDER_GRADUAL_SHAPE_GRADIENT: 'border-gradual-shape-gradient',
    BORDER_GRADUAL_ANGLE: 'border-gradual-angle',
    BORDER_GRADUAL_ANGLE_TEXT: 'border-gradual-angle-text',
    BORDER_GRADUAL_DIRECTION: 'border-gradual-direction',
    BORDER_GRADUAL_DIRECTION_TEXT: 'border-gradual-direction-text',
    BORDER_GRADUAL_BAR: 'border-gradual-bar',
    BORDER_GRADUAL_OPACITY: 'border-gradual-opacity',
    BORDER_SPACE: 'border-space',
    BORDER_COMPOUND_TYPE_TEXT: 'border-compound-type-text',
    BORDER_COMPOUND_TYPE: 'border-compound-type',
    /** 动画 */
    ANIMATION_EDITOR: 'animation-editor',
    /** 间隔 */
    SPACE: 'space',
    /** 间隔 */
    BREAK: 'break',
    /** 间隔 */
    PUSHER: 'pusher',
    /** 表格 */
    TABLE_BORDER_COLOR: 'table-border-color',
    TABLE_BORDER_COLOR_TEXT: 'table-border-color-text',
    /** 单元格大小 */
    CELL_WIDTH: 'cell_width',
    CELL_HEIGHT: 'cell_height',
    /** 大小 */
    WIDTH: 'width',
    HEIGHT: 'height',
    SCALE_WIDTH: 'scale-width',
    SCALE_HEIGHT: 'scale-height',
    RELATIVE_IMAGE_ORIGINAL_SIZE: 'relative-image-original-size',   // 相对于图片原始尺寸
    BEST_PLAY_SCALE: 'best-play-scale', // 幻灯片最佳放映比例
    ORIGINAL_SIZE: 'original-size', // 原始尺寸
    RESET_ORIGINAL_SIZE: 'reset-original-size', // 重设
    ORIGINAL_SIZE_CONTENT: 'original-size-content', // 原始尺寸内容
    RESOLUTION: 'resolution', // 分辨率
    RESOLUTION_TEXT: 'resolution-text', // 分辨率文字
    /** 母版编辑 幻灯片比例 */
    SLIDE_SIZE: 'slide-size',
    SLIDE_SIZE_SELECT: 'slide-size-select',
    /** 母版编辑 背景 */
    BACKGROUND: 'background',
    /** 母版编辑 展示母版元素 */
    SHOW_MASTER_SHAPE: 'show-master-shape',
    /** 母版编辑 版式 */
    LAYOUT: 'layout',
    /** 母版编辑 版式 占位符checkbox 选项 */
    PLACEHOLDER_FORMAT: 'placeholder-format',
    /** 幻灯片编辑 切换版式 */
    LAYOUT_CHANGE: 'layout-change',
    /** 幻灯片编辑 页脚标题 */
    FOOTER: 'footer',
    /** 幻灯片编辑 页码 */
    SLIDE_NUMBER: 'slide-number',
    /** 幻灯片编辑 页脚 */
    SLIDE_FOOTER: 'slide-footer',
    /** 幻灯片编辑 页脚输入框 */
    SLIDE_FOOTER_INPUT: 'slide-footer-input',
    /** 幻灯片编辑 日期 */
    DATETIME: 'datetime',
    /** 幻灯片编辑 日期类型选择框 */
    DATETIME_TYPE_SELECT: 'datetime-type-select',
    /** 幻灯片编辑 日期手动输入框 */
    DATETIME_INPUT: 'datetime-input',
    /** 幻灯片编辑 日期格式选择框 */
    DATETIME_FIELD_TYPE_SELECT: 'datetime-field-type-input',
    SLIDE_NUMBER_START: 'slide-number-start',
    SLIDE_NUMBER_START_TEXT: 'slide-number-start-text',
    FOOTER_APPLY: 'footer-apply',
    /** 图片填充-充满偏移 */
    IMAGE_FILL_OFFSET_X: 'image-fill-offset-x',
    IMAGE_FILL_OFFSET_Y: 'image-fill-offset-y',
    IMAGE_FILL_SCALE_X: 'image-fill-scale-x',
    IMAGE_FILL_SCALE_Y: 'image-fill-scale-y',
    /** 图片填充-平铺偏移 */
    IMAGE_FILL_OFFSET_LEFT: 'image-fill-offset-left',
    IMAGE_FILL_OFFSET_RIGHT: 'image-fill-offset-right',
    IMAGE_FILL_OFFSET_TOP: 'image-fill-offset-top',
    IMAGE_FILL_OFFSET_BOTTOM: 'image-fill-offset-bottom',
};

const FormItemLabelName = {
    SIZE: i18n.t('大小'),
    BLUR: i18n.t('模糊'),
    DIST: i18n.t('距离'),
    OPACITY: i18n.t('透明度'),
    COLOR: i18n.t('颜色'),
    LINEAR_GRADIENT: i18n.t('线性'),
    RADIAL_GRADIENT: i18n.t('射线'),
    RECT_GRADIENT: i18n.t('矩形'),
    SHAPE_GRADIENT: i18n.t('路径'),
    TITLE_GRADIENT: i18n.t('标题的阴影'),
    TYPE: i18n.t('类型'),
    DIRECTION: i18n.t('方向'),
    SELECT_IMAGE: i18n.t('选择图片'),
    CLIPBOARD: i18n.t('剪贴板'),
    ANGLE: i18n.t('角度'),
    ANGLE_X: i18n.t('X 旋转'),
    ANGLE_Y: i18n.t('Y 旋转'),
    ANGLE_Z: i18n.t('Z 旋转'),
    ROTATION: i18n.t('三维旋转'),
    SHARPEN_SOFTEN: i18n.t('清晰度'),
    BRIGHTNESS: i18n.t('亮度'),
    CONSTRAST: i18n.t('对比度'),
    SATURATE: i18n.t('饱和度'),
    COLOR_TEMPERATURE: i18n.t('色温'),
    PICTURE_RECOLOR: i18n.t('重新着色'),
    LEFT: i18n.t('左'),
    RIGHT: i18n.t('右'),
    TOP: i18n.t('上'),
    BOTTOM: i18n.t('下'),
    SIZE_ADJUST: i18n.t('锁定纵横比'),
    FONT_AUTO_BREAK: i18n.t('形状中的文字自动换行'),
    ROTATE_WITH_SHAPE: i18n.t('与形状一起旋转'),
    LINE_END: i18n.t('箭头类型'),
    LINE_SIZE_TYPE: i18n.t('箭头粗细'),
    WIDTH: i18n.t('宽度'),
    HEIGHT: i18n.t('高度'),
    SCALE_WIDTH: i18n.t('缩放宽度'),
    SCALE_HEIGHT: i18n.t('缩放高度'),
    FONT_THEME: i18n.t('字体'),
    SHOW_MASTER_SHAPE: i18n.t('隐藏背景图形'),
    RESET_BACKGROUND: i18n.t('重置背景'),
    APPLY_TO_ALL_SLIDE: i18n.t('应用到全部'),
    SLIDE_SIZE_SELECT: i18n.t('幻灯片比例'),
    SLIDE_NUMBER: i18n.t('页码'),
    SLIDE_FOOTER: i18n.t('页脚'),
    DATETIME: i18n.t('日期'),
    HORIZONTAL_POSITION: i18n.t('水平位置'),
    VERTICAL_POSITION: i18n.t('垂直位置'),
    RELATIVE_IMAGE_ORIGINAL_SIZE: i18n.t('相对于图片原始尺寸'),
    BEST_PLAY_SCALE: i18n.t('幻灯片最佳放映比例'),
    RESET_ORIGINAL_SIZE: i18n.t('重设'),
    ORIGINAL_SIZE: i18n.t('原始尺寸'),
    RESOLUTION: i18n.t('分辨率'),
    SKETCH: i18n.t('草绘样式'),
    PICTURE_POSITION: i18n.t('图片位置'),
    CROP_POSITION: i18n.t('裁剪位置'),
    OFFSET_X: i18n.t('偏移量 X'),
    OFFSET_Y: i18n.t('偏移量 Y'),
    DEPTH: i18n.t('深度'),
    STYLE_3D: i18n.t('三维样式'),
    DEPTH_COLOR_3D: i18n.t('深度颜色'),
    SHOW_FLAT_TEXT: i18n.t('保持文本平面状态'),
    FRUSTUM_3D: i18n.t('棱台'),
    FRONT_FRUSTUM: i18n.t('前棱台'),
    BACK_FRUSTUM: i18n.t('后棱台'),
    FRUSTUM_WIDTH: i18n.t('宽度'),
    FRUSTUM_HEIGHT: i18n.t('高度'),
    LIGHT_3D: i18n.t('光源'),
    LIGHT_TYPE_3D: i18n.t('光源类型'),
    LIGHT_ROTATION: i18n.t('光源角度'),
    MATERIAL_3D: i18n.t('材料'),
    MATERIAL_TYPE_3D: i18n.t('材料类型'),
    CAMERA_PRESET_3D: i18n.t('旋转预设'),
    SCALE_X: i18n.t('刻度 X'),
    SCALE_Y: i18n.t('刻度 Y'),
    OFFSET_LEFT: i18n.t('向左偏移'),
    OFFSET_RIGHT: i18n.t('向右偏移'),
    OFFSET_TOP: i18n.t('向上偏移'),
    OFFSET_BOTTOM: i18n.t('向下偏移'),
    BORDER_COMPOUND_TYPE: i18n.t('复合类型'),
    SERIES_OVERLAP: i18n.t('系列重叠'),
    GAP_WIDTH: i18n.t('间隙宽度'),
    ANGLE_OF_FIRST_SLICE: i18n.t('第一扇区起始角度'),
    DOUGHNUT_HLOE_SIZE: i18n.t('圆环图圆环大小'),
    BUBBLE_SIZE_REORESENTS: i18n.t('大小表示'),
};

export const ChartComponentPanelMapping = {
    [ChartElementType.TITLE]: FormPanelKey.CHART_TITLE_PAGE,
    [ChartElementType.LEGEND]: FormPanelKey.CHART_LEGEND_PAGE,
    [ChartElementType.AXIS_TITLE]: FormPanelKey.CHART_AXIS_TITLE_PAGE,
    [ChartElementType.DATA_LABEL]: FormPanelKey.CHART_DATA_LABEL_PAGE,
    [ChartElementType.MAJOR_GRID]: FormPanelKey.CHART_GRID_LINE_PAGE,
    [ChartElementType.MINOR_GRID]: FormPanelKey.CHART_GRID_LINE_PAGE,
    [ChartElementType.VALUE_AXIS_LABEL]: FormPanelKey.CHART_VALUE_AXIS_PAGE,
    [ChartElementType.NOT_VALUE_AXIS_LABEL]: FormPanelKey.CHART_NOT_VALUE_AXIS_PAGE,
};

enum FormItemType {
    CUSTOM = 'custom',
    BUTTON = 'button',
    COLOR = 'color',
    ANGLE = 'angle',
    SELECT = 'select',
    SLIDER = 'slider',
    TEXT = 'text',
    CHECKBOX = 'checkbox',
    SPIN = 'spin',
}

// workbench事件
enum WbEventNames {
    RIGHT_DRAWER_CLOSED = '$rightDrawerClosed', // 关闭右侧抽屉动画结束
    FORM_NUMBER_INPUT_BLUR = '$formNumberInputBlur',    // 右侧输入框聚焦
    FORM_NUMBER_INPUT_FOCUS = '$formNumberInputFocus',  // 右侧输入框失焦
}

export {
    DrawerName,
    FloatName,
    FormPanelName,
    FormLabelName,
    FormItemName,
    FormItemLabelName,
    FormItemType,
    FormSectionCollapsed,
    WbEventNames,
};

export const MULTI_DIFF_VALUE = '';
export type IMultiDiffValue = typeof MULTI_DIFF_VALUE;

/**
 * 输入框回调的key值
 */
export const INPUT_PROPS_CALLBACK_KEY = {
    FILL: 'fill',
    SOLID_FILL: 'solid-fill',
    GRADUAL_FILL: 'gradual-fill',
    IMAGE_FILL: 'image-fill',
    COMMON_FILL: 'common-fill',
    BORDER: 'border',
    SHADOW: 'shadow',
    GLOW: 'glow',
    REFLECTION: 'reflection',
    SOFT_EDGE: 'soft-edge',
    SIZE: 'size',
    PICTURE_COLOR: 'picture-color',
    PICTURE_OPACITY: 'picture-opacity',
    REGULATE: 'regulate',
    TABLE_BORDER: 'table-border',
    TEXT_BOX: 'text-box',
    TEXT_SHADOW: 'text-shadow',
    TEXT_GLOW: 'text-glow',
    TEXT_REFLECTION: 'text-reflection',
    TEXT_SOFT_EDGE: 'text-soft-edge',
    CHART_VALUE_AXIS: 'chart-value-axis',
    POSITION: 'position',
    PICTURE_POSITIOIN: 'picture-position',
    ROTATE: 'rotate',
    TEXT_FILL: 'text-fill',
    TEXT_BORDER: 'text-border',
    THREE_D: '3-d',
    TABLE_ROW_COL_SIZE: 'table-row-col-size',
    SLIDE_FOOTER: 'slide-footer',
    CHART_FILL: 'chart-fill',
    CHART_BORDER: 'chart-border',
    CHART_GLOBAL_BORDER: 'chart-global-border',
    CHART_GLOBAL_FILL: 'chart-global-fill',
    CHART_GLOBAL_SHADOW: 'chart-global-shadow',
    CHART_GLOBAL_GLOW: 'chart-global-glow',
    CHART_GLOBAL_SOFT_EDGE: 'chart-global-soft-edge',
    CHART_SERIES_OPTIONS: 'chart-series-options',
};
